import {
  ICounterpartyOrdersExportParams,
  ICounterpartyOrdersParams,
  IExportOrdersParams,
  IGetOrdersImportsParams,
  IOrderByProcessParams,
  IOrderByProjectParams,
  IOrderImportsParams,
  IOrdersParams,
  IProjectsParams,
} from '@app/models/interfaces';
import {
  ActionKind,
  AddModuleSchema,
  ApplyActionSchema,
  ApproveOrderParamsDTO,
  CancelOrderParamsDTO,
  ContextSchema,
  CounterpartyOrderCustomerSchema,
  CounterpartyOrderSchema,
  CounterpartyOrdersCountsSchema,
  CreateAccessSettingsDTO,
  CreateOrderDTO,
  CreateProcessDTO,
  CreateProjectDTO,
  CreateStageStatusDTO,
  EpdConfirmedSigningSchema,
  EpdConfirmSignatureSchema,
  EpdOrderTabSchema,
  EpdPreparedSigningSchema,
  EpdPrepareSignatureSchema,
  EpdRegistryFilters,
  EpdSigningContextRequestSchema,
  EpdSigningContextSchema,
  EpdSigningCountSchema,
  EpdSigningFileSchema,
  GetMappingSettingsParamsDTO,
  MappingSettingsDTO,
  MoveMultipleToProjectDTO,
  MoveToProjectDTO,
  MultipleMoveToStatusSchema,
  OrderExportResponseSchema,
  OrderHistorySchema,
  OrderImportDataSchema,
  OrderImportingStateSchema,
  OrderImportSchema,
  PartialUpdateStageSchema,
  PartialUpdateStageStatusSchema,
  PreviewDTO,
  PreviewParamsDTO,
  ProcessAccessSettingsSchema,
  ProcessStageSchema,
  ProcessStageStatusSchema,
  RearrangeFieldDTO,
  RearrangeStagesDTO,
  RearrangeStatusesDTO,
  RecordsListGenericSchemaCounterpartyOrderSchema,
  RecordsListGenericSchemaCounterpartyWarehouseOrderSchema,
  RecordsListGenericSchemaEpdRegistryRowSchema,
  RecordsListGenericSchemaOrderImportDataSchema,
  RecordsListGenericSchemaOrderImportSchema,
  RecordsListGenericSchemaRetrieveOrderSchema,
  RecordsListGenericSchemaRetrieveProjectSchema,
  RecordsListGenericSchemaSimpleProcessSchema,
  RemoveModuleSchema,
  RetrieveOrderSchema,
  RetrieveProcessSchema,
  RetrieveProjectSchema,
  RunOrderImportParamsDTO,
  UpdateModuleSettingsSchema,
  UpdateOrderParamsDTO,
  UpdateProcessAccessSettingsDTO,
  UpdateProjectDTO,
} from '@app/models/orders';
import { handleRequest, httpClient } from '@app/services/httpClient';
import { objectToSearchParams } from '@app/services/objectToSearchParams';

import { IProps } from '../interfaces';

class Orders {
  private readonly endPoint: string;

  constructor({ baseEndPoint }: IProps) {
    this.endPoint = baseEndPoint;
  }

  async getEpdDocuments(params: EpdRegistryFilters): Promise<RecordsListGenericSchemaEpdRegistryRowSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/epd/documents${objectToSearchParams(params)}`));
  }

  async getEpdSigningCount(): Promise<EpdSigningCountSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/epd/signing-requests/count`));
  }

  async getEpdOrderTab(uid: string): Promise<EpdOrderTabSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/epd/orders/${uid}`));
  }

  async getEpdTitleFile(uid: string, kind: 'file' | 'signature') {
    return httpClient.get<Blob>(`${this.endPoint}/epd/titles/${uid}/${kind}`, { responseType: 'blob' });
  }

  async postEpdSigningContext(data: EpdSigningContextRequestSchema): Promise<EpdSigningContextSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/epd/signing-context`, data));
  }

  async prepareEpdSigning(uid: string, signal?: AbortSignal): Promise<EpdPreparedSigningSchema> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/epd/signing-requests/${uid}/prepare`, undefined, { signal, timeout: 60000 })
    );
  }

  async prepareEpdSignature(
    uid: string,
    data: EpdPrepareSignatureSchema,
    signal?: AbortSignal
  ): Promise<EpdSigningFileSchema> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/epd/signing-requests/${uid}/prepare-signature`, data, {
        signal,
        timeout: 60000,
      })
    );
  }

  async confirmEpdSignature(
    uid: string,
    data: EpdConfirmSignatureSchema,
    signal?: AbortSignal
  ): Promise<EpdConfirmedSigningSchema> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/epd/signing-requests/${uid}/confirm-signature`, data, {
        signal,
        timeout: 60000,
      })
    );
  }

  async resetEpdSigningRequest(uid: string): Promise<void> {
    return handleRequest(httpClient.post(`${this.endPoint}/epd/signing-requests/${uid}/reset`));
  }

  async postOrder(cart_uid: string, data: CreateOrderDTO): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/carts/${cart_uid}/create-order`, data));
  }

  async postProcess(data: CreateProcessDTO): Promise<RetrieveProcessSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes`, data));
  }

  async postProcessStage(uid: string, data: CreateProcessDTO): Promise<ProcessStageSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${uid}/stages`, data));
  }

  async postAddModule(uid: string, data: AddModuleSchema): Promise<any> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${uid}/modules`, data));
  }

  async postStageStatus(uid: string, data: CreateStageStatusDTO): Promise<ProcessStageStatusSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/stages/${uid}/statuses`, data));
  }

  async deleteStageStatus(stage_uid: string, status_uid: string): Promise<ProcessStageStatusSchema> {
    return handleRequest(httpClient.delete(`${this.endPoint}/stages/${stage_uid}/statuses/${status_uid}`));
  }

  async deleteModule(process_uid: string, data: RemoveModuleSchema): Promise<ProcessStageStatusSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${process_uid}/remove-module`, data));
  }

  async postProcessRearrangeStage(uid: string, data: RearrangeStagesDTO): Promise<RetrieveProcessSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${uid}/rearrange-stages`, data));
  }

  async postStageRearrangeStatus(stageUid: string, data: RearrangeStatusesDTO): Promise<RetrieveProcessSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/stages/${stageUid}/rearrange-statuses`, data));
  }

  async updateProcess(process_uid: string, data: CreateProcessDTO): Promise<RetrieveProcessSchema> {
    return handleRequest(httpClient.put(`${this.endPoint}/processes/${process_uid}`, data));
  }

  async getOrders(params: IOrdersParams): Promise<RecordsListGenericSchemaRetrieveOrderSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/orders${objectToSearchParams(params)}`));
  }

  async getOrderImports(params: IOrderImportsParams): Promise<RecordsListGenericSchemaOrderImportSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/order-imports${objectToSearchParams(params)}`));
  }

  async getOrderImport(uid: string): Promise<OrderImportSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/order-imports/${uid}`));
  }

  async postGetSignature(uid: string): Promise<{ signature: string }> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports/${uid}/get-file-signature`));
  }

  async getProject(project_uid: string): Promise<RetrieveProjectSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/projects/${project_uid}`));
  }

  async getProjects(params: IProjectsParams): Promise<RecordsListGenericSchemaRetrieveProjectSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/projects${objectToSearchParams(params)}`));
  }

  async postCreateProject(data: CreateProjectDTO): Promise<RetrieveProjectSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/projects`, data));
  }

  async putUpdateProject(project_uid: string, data: UpdateProjectDTO): Promise<RetrieveProjectSchema> {
    return handleRequest(httpClient.put(`${this.endPoint}/projects/${project_uid}`, data));
  }

  async getUploadedFile({
    uploaded_file_uid,
    signature,
  }: {
    uploaded_file_uid: string;
    signature: string;
  }): Promise<any> {
    return handleRequest(httpClient.get(`${this.endPoint}/uploaded-files/${uploaded_file_uid}/download/${signature}`));
  }

  async getOrderImportData({
    order_import_uid,
    ...rest
  }: IGetOrdersImportsParams): Promise<RecordsListGenericSchemaOrderImportDataSchema> {
    return handleRequest(
      httpClient.get(
        `${this.endPoint}/order-imports/${order_import_uid}/order-import-data${objectToSearchParams(rest)}`
      )
    );
  }

  async getOrder(order_uid: string): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/orders/${order_uid}`));
  }

  async getHistory(order_uid: string): Promise<OrderHistorySchema[]> {
    return handleRequest(httpClient.get(`${this.endPoint}/orders/${order_uid}/history`));
  }

  async cancelOrder(order_uid: string, data: CancelOrderParamsDTO): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/${order_uid}/cancel`, data));
  }

  async approveOrder(order_uid: string, data: ApproveOrderParamsDTO): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/${order_uid}/approve`, data));
  }

  async markAsNeedWork(order_uid: string, data: ApproveOrderParamsDTO): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/${order_uid}/mark-as-needs-work`, data));
  }

  async applyAction(data: ApplyActionSchema, order_uid: string, kind: ActionKind): Promise<string> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/${order_uid}/actions/${kind}`, data));
  }

  async getContext(): Promise<ContextSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/context`));
  }

  async getCounterpartyOrders(
    params: ICounterpartyOrdersParams
  ): Promise<RecordsListGenericSchemaCounterpartyOrderSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/counterparty/orders${objectToSearchParams(params)}`));
  }

  async getCounterpartyWarehouseOrders(
    params: ICounterpartyOrdersParams
  ): Promise<RecordsListGenericSchemaCounterpartyWarehouseOrderSchema> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/counterparty/warehouse-orders${objectToSearchParams(params)}`)
    );
  }

  async getCounterpartyDeliveryOrders(
    params: ICounterpartyOrdersParams
  ): Promise<RecordsListGenericSchemaCounterpartyOrderSchema> {
    return handleRequest(
      httpClient.get(`${this.endPoint}/counterparty/delivery-orders${objectToSearchParams(params)}`)
    );
  }

  async exportCounterpartyOrders(params: ICounterpartyOrdersExportParams): Promise<OrderExportResponseSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/counterparty/orders/export${objectToSearchParams(params)}`));
  }

  async getCounterpartyOrderCustomers(): Promise<CounterpartyOrderCustomerSchema[]> {
    return handleRequest(httpClient.get(`${this.endPoint}/counterparty/orders/customers`));
  }

  async getCounterpartyOrdersCounts(): Promise<CounterpartyOrdersCountsSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/counterparty/orders/counts`));
  }

  async getCounterpartyOrder(order_uid: string): Promise<CounterpartyOrderSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/counterparty/orders/${order_uid}`));
  }

  async updateOrder(order_uid: string, data: UpdateOrderParamsDTO): Promise<RetrieveOrderSchema> {
    return handleRequest(httpClient.put(`${this.endPoint}/orders/${order_uid}`, data));
  }

  async exportOrders({ uid, ...params }: IExportOrdersParams): Promise<OrderExportResponseSchema> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/processes/${uid}/export-orders${objectToSearchParams(params)}`)
    );
  }

  async getProcesses(): Promise<RecordsListGenericSchemaSimpleProcessSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/processes`));
  }

  async getProcess(uid: string): Promise<RetrieveProcessSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/processes/${uid}`));
  }

  async getProcessAccessSettings(uid: string): Promise<ProcessAccessSettingsSchema[]> {
    return handleRequest(httpClient.get(`${this.endPoint}/processes/${uid}/access-settings`));
  }

  async getOrdersByProcess({
    process_uid,
    ...rest
  }: IOrderByProcessParams): Promise<RecordsListGenericSchemaRetrieveOrderSchema> {
    return handleRequest(
      httpClient.get(
        `${this.endPoint}/processes/${process_uid}/orders${objectToSearchParams({
          ...rest,
        })}`
      )
    );
  }

  async getOrdersByProject({
    project_uid,
    ...rest
  }: IOrderByProjectParams): Promise<RecordsListGenericSchemaRetrieveOrderSchema> {
    return handleRequest(
      httpClient.get(
        `${this.endPoint}/projects/${project_uid}/orders${objectToSearchParams({
          ...rest,
        })}`
      )
    );
  }

  async postMoveToStatus(order_uid: string, status_uid: string, version: string): Promise<null> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/orders/${order_uid}/move-to-status`, {
        to_status_uid: status_uid,
        version,
      })
    );
  }

  async postMultipleMoveToStatus(data: MultipleMoveToStatusSchema, process_uid: string): Promise<null> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${process_uid}/multiple-move-to-status`, data));
  }

  async putStages(stage_uid: string, name: string): Promise<null> {
    return handleRequest(httpClient.patch(`${this.endPoint}/stages/${stage_uid}`, { name }));
  }

  async patchStatus(status_uid: string, data: PartialUpdateStageStatusSchema): Promise<null> {
    return handleRequest(httpClient.patch(`${this.endPoint}/statuses/${status_uid}`, data));
  }

  async deleteFieldGroup(field_group_uid: string): Promise<null> {
    return handleRequest(httpClient.delete(`${this.endPoint}/field-groups/${field_group_uid}`));
  }

  async postRearrangeFieldGroup(field_group_uid: string, data: RearrangeFieldDTO): Promise<null> {
    return handleRequest(httpClient.post(`${this.endPoint}/field-groups/${field_group_uid}/rearrange-field`, data));
  }

  async deleteField(field_uid: string): Promise<null> {
    return handleRequest(httpClient.delete(`${this.endPoint}/fields/${field_uid}`));
  }

  async deleteAccessSettingsGroup(group_uid: string): Promise<null> {
    return handleRequest(httpClient.delete(`${this.endPoint}/access-settings/${group_uid}`));
  }

  async deleteProcessStage(process_uid: string, stage_uid: string): Promise<null> {
    return handleRequest(httpClient.delete(`${this.endPoint}/processes/${process_uid}/stages/${stage_uid}`));
  }

  async createFieldGroup(order_structure_uid: string, name: string): Promise<null> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/order-structures/${order_structure_uid}/field-groups`, {
        name,
      })
    );
  }

  async createAccessProcessSettings(process_uid: string, data: CreateAccessSettingsDTO): Promise<null> {
    return handleRequest(httpClient.post(`${this.endPoint}/processes/${process_uid}/access-settings`, data));
  }

  async putFieldGroup(field_group_uid: string, name: string): Promise<null> {
    return handleRequest(httpClient.put(`${this.endPoint}/field-groups/${field_group_uid}`, { name }));
  }

  async putAccessSettingsGroup(uid: string, data: UpdateProcessAccessSettingsDTO): Promise<null> {
    return handleRequest(httpClient.put(`${this.endPoint}/access-settings/${uid}`, data));
  }

  async putField(field_uid: string, name: string, type: string, is_required: boolean): Promise<null> {
    return handleRequest(
      httpClient.put(`${this.endPoint}/fields/${field_uid}`, { type, name, settings: null, is_required })
    );
  }

  async postImportsPreview(data: PreviewParamsDTO): Promise<PreviewDTO> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports/preview`, data));
  }

  async postImportsRun(data: RunOrderImportParamsDTO): Promise<OrderImportSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports/run`, data));
  }

  async postGetSettingsMapping(data: GetMappingSettingsParamsDTO): Promise<MappingSettingsDTO> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports/get-mapping-settings`, data));
  }

  async postUpdateOrderImportData(data: object, order_import_data_uid: string): Promise<OrderImportDataSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports-data/${order_import_data_uid}/update`, data));
  }

  async postCreateOrders(order_import_uid: string): Promise<OrderImportingStateSchema> {
    return handleRequest(httpClient.post(`${this.endPoint}/order-imports/${order_import_uid}/create-orders`));
  }

  async getImportState(order_importing_state_uid: string): Promise<OrderImportingStateSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/order-importing-states/${order_importing_state_uid}`));
  }

  async getUploadState(order_importing_state_uid: string): Promise<OrderImportingStateSchema> {
    return handleRequest(httpClient.get(`${this.endPoint}/order-importing-states/${order_importing_state_uid}`));
  }

  async postMoveToProject(order_uid: string, data: MoveToProjectDTO): Promise<any> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/${order_uid}/move-to-project`, data));
  }

  async postMultipleMoveToProject(data: MoveMultipleToProjectDTO): Promise<any> {
    return handleRequest(httpClient.post(`${this.endPoint}/orders/move-multiple-to-project`, data));
  }

  async postArchiveProject(project_uid: string): Promise<any> {
    return handleRequest(httpClient.post(`${this.endPoint}/projects/${project_uid}/archive`));
  }

  async postRestoreProject(project_uid: string): Promise<any> {
    return handleRequest(httpClient.post(`${this.endPoint}/projects/${project_uid}/restore`));
  }

  async patchOrderProcessModel(processUid: string, moduleUid: string, data: UpdateModuleSettingsSchema): Promise<any> {
    return handleRequest(httpClient.patch(`${this.endPoint}/processes/${processUid}/modules/${moduleUid}`, data));
  }

  async createField({
    field_group_uid,
    name,
    type,
    is_required,
  }: {
    field_group_uid: string;
    type: string;
    name: string;
    is_required: boolean;
  }): Promise<null> {
    return handleRequest(
      httpClient.post(`${this.endPoint}/field-groups/${field_group_uid}/fields`, {
        name,
        type,
        is_required,
        settings: null,
      })
    );
  }
}

export default Orders;
